import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import axios from "axios";
import { API_BASE_URL } from "../../config/constants";
import {
  COUPON_CATEGORIES,
  APPLY_BY_OPTIONS,
  COUPON_RULES,
} from "../../config/constantselections";
import ApplyListComponent from "./ApplyListComponent";

const CouponManagement = () => {
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState({
    name: "",
    expirydate: "",
    category: "GENERAL",
    rule: "",
    active: true,
    applyby: "",
    applylist: [],
  });
  const [editIndex, setEditIndex] = useState(null);

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/coupons`);
      setCoupons(res.data);
    } catch (err) {
      console.error("Failed to fetch coupons", err);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleAddOrUpdate = async () => {
    if (!form.name || !form.expirydate) return;

    try {
      if (editIndex !== null) {
        const updated = [...coupons];
        const id = updated[editIndex].id;
        await axios.put(`${API_BASE_URL}/coupons/${id}`, form);
        updated[editIndex] = { ...form, id };
        setCoupons(updated);
        setEditIndex(null);
      } else {
        const res = await axios.post(`${API_BASE_URL}/coupons`, form);
        setCoupons((prev) => [...prev, res.data]);
      }

      setForm({
        name: "",
        expirydate: "",
        category: "GENERAL",
        rule: "",
        active: true,
        applyby: "",
        applylist: [],
      });
    } catch (err) {
      console.error("Save failed", err);
    }
  };

  const handleEdit = (index) => {
    setForm(coupons[index]);
    setEditIndex(index);
  };

  const handleDelete = async (index) => {
    const id = coupons[index].id;
    await axios.delete(`${API_BASE_URL}/coupons/${id}`);
    setCoupons(coupons.filter((_, i) => i !== index));
  };

  return (
    <Box>
      <h2>Coupon Management</h2>
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Coupon Name"
            name="name"
            value={form.name}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="date"
            label="Expiry Date"
            name="expirydate"
            InputLabelProps={{ shrink: true }}
            value={form.expirydate}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              name="category"
              value={form.category}
              label="Category"
              onChange={handleChange}
            >
              {COUPON_CATEGORIES.map((cat) => (
                <MenuItem key={cat.value} value={cat.value}>
                  {cat.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {form.category === "CUSTOM" && (
          <>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Apply By</InputLabel>
                <Select
                  name="applyby"
                  value={form.applyby}
                  label="Apply By"
                  onChange={handleChange}
                >
                  {APPLY_BY_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <ApplyListComponent
                applyBy={form.applyby}
                applyList={form.applylist}
                onChange={(list) =>
                  setForm((prev) => ({ ...prev, applylist: list }))
                }
              />
            </Grid>
          </>
        )}
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Rule</InputLabel>
            <Select
              name="rule"
              value={form.rule}
              label="Rule"
              onChange={handleChange}
            >
              {COUPON_RULES.map((rule) => (
                <MenuItem key={rule.value} value={rule.value}>
                  {rule.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                name="active"
                checked={form.active}
                onChange={handleChange}
              />
            }
            label="Active"
          />
        </Grid>
      </Grid>

      <Button variant="contained" onClick={handleAddOrUpdate}>
        {editIndex !== null ? "Update Coupon" : "Add Coupon"}
      </Button>

      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Expiry</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Rule</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>ApplyBy</TableCell>
              <TableCell>ApplyList</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {coupons.map((coupon, index) => (
              <TableRow key={coupon.id || index}>
                <TableCell>{coupon.name}</TableCell>
                <TableCell>{coupon.expirydate}</TableCell>
                <TableCell>{coupon.category}</TableCell>
                <TableCell>
                  {
                    COUPON_RULES.find((r) => r.value === coupon.rule)?.label ||
                    coupon.rule
                  }
                </TableCell>
                <TableCell>{coupon.active ? "Yes" : "No"}</TableCell>
                <TableCell>{coupon.applyby || "-"}</TableCell>
                <TableCell>
                  {(coupon.applylist || [])
                    .map((item) => (typeof item === "string" ? item : item.value))
                    .join(", ") || "-"}
                </TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => handleEdit(index)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(index)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default CouponManagement;
